import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  /** 0-indexed current page. */
  page: number;
  /** Total number of pages. The control hides itself when this is ≤ 1. */
  totalPages: number;
  /**
   * Called with the next 0-indexed page, already clamped to
   * `[0, totalPages - 1]`. The caller owns the page state.
   */
  onPageChange: (page: number) => void;
  /**
   * Hit-target size:
   *   - `md` (default): 40px buttons / 24px chevrons — matches the
   *     onboarding Approved Senders list.
   *   - `sm`: 32px buttons / 20px chevrons — fits the narrower walkthrough
   *     modal.
   */
  size?: 'sm' | 'md';
  className?: string;
}

const SIZES = {
  sm: { button: 'w-8 h-8', icon: 20 },
  md: { button: 'w-10 h-10', icon: 24 },
} as const;

/**
 * Prev / "{current}/{total}" / Next pager shared by the onboarding
 * Approved Senders list and the Today Tab walkthrough sender list
 * (YAR-238 AC 13). Renders nothing for a single page so callers can mount
 * it unconditionally. The caller keeps owning `page` state; this only
 * renders the control and emits clamped page indices.
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  size = 'md',
  className = '',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const { button, icon } = SIZES[size];
  const prevDisabled = page <= 0;
  const nextDisabled = page >= totalPages - 1;

  return (
    <div
      className={['flex items-center justify-center gap-5', className]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={prevDisabled}
        aria-label="Previous page"
        className={`${button} flex items-center justify-center rounded-sm disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        <ChevronLeft
          size={icon}
          className="text-icon-default"
        />
      </button>
      <p
        className="text-sm text-foreground text-center min-w-[2.5rem]"
        aria-live="polite"
      >
        {page + 1}/{totalPages}
      </p>
      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        disabled={nextDisabled}
        aria-label="Next page"
        className={`${button} flex items-center justify-center rounded-sm disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        <ChevronRight
          size={icon}
          className="text-icon-default"
        />
      </button>
    </div>
  );
}
